/* ============================================================
   SAM-944 — Activity at 375 px (canvas PhoneActivity*): pure helpers for the time capsules, no React.
   The phone has Today / This week / This month / Custom dates in place of the desktop's per-view pills; each one
   is just a DayRange, so the view's existing `range` state (and everything it feeds) stays the only source.
   ============================================================ */
import { toDayParam } from '../../api/businessAccessCombined';
import type { ActivityView, DayRange, TxPill, VisitPill } from './activityViews';
import { hasRange } from './activityViews';

export type PhoneTime = 'today' | 'week' | 'month';

const day0 = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

/** Monday to Sunday of the week `now` falls in. */
export function weekRange(now: Date = new Date()): DayRange {
  const today = day0(now);
  const monday = new Date(today);
  monday.setDate(today.getDate() - ((today.getDay() + 6) % 7));
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return { from: toDayParam(monday), to: toDayParam(sunday) };
}

/** The whole calendar month of `now`. */
export function monthRange(now: Date = new Date()): DayRange {
  return {
    from: toDayParam(new Date(now.getFullYear(), now.getMonth(), 1)),
    to: toDayParam(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
  };
}

export function phoneTimeRange(k: PhoneTime, now: Date = new Date()): DayRange {
  if (k === 'today') return { from: toDayParam(now), to: toDayParam(now) };
  return k === 'week' ? weekRange(now) : monthRange(now);
}

const same = (a: DayRange, b: DayRange) => a.from === b.from && a.to === b.to;

/**
 * Which capsule is on. A chosen range lights its capsule (or «Custom dates» when it is none of the three); with
 * no range the view's own default pill does (Today for Bookings and Passes, This month for Transactions).
 */
export function activePhoneTime(
  view: ActivityView, range: DayRange, visitPill: VisitPill, txPill: TxPill | null, now: Date = new Date(),
): PhoneTime | 'custom' | null {
  if (hasRange(range)) {
    const hit = (['today', 'week', 'month'] as PhoneTime[]).find((k) => same(range, phoneTimeRange(k, now)));
    return hit ?? 'custom';
  }
  if (view === 'transactions') return txPill === 'today' ? 'today' : txPill === 'month' ? 'month' : null;
  return visitPill === 'today' ? 'today' : null;
}
